const chapter = {
  id: 'laws-of-motion',
  slug: 'laws-of-motion',
  subject: 'physics',
  subjectLabel: 'Physics',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Laws of Motion',
        hindi: 'गति के नियम',
  },
  estReadMinutes: 60,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '2–3 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/एनईईटी में 2-3 प्रश्न',
  },
  syllabusTags: ['Mechanics', 'JEE-Main', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'newton',
      title: {
        english: 'Newton\'s Three Laws',
        hindi: 'न्यूटन के तीन नियम',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'First Law (Inertia)',
        hindi: 'पहला नियम (जड़ता)',
          },
          statement: {
            english: 'Every body continues in its state of rest or uniform motion in a straight line unless acted upon by a net external force. Defines an **inertial frame** as one in which this law holds.',
        hindi: 'प्रत्येक पिंड अपनी विश्राम अवस्था या एक सीधी रेखा में एकसमान गति में तब तक बना रहता है जब तक उस पर कोई बाहरी बल न लगाया जाए। एक **जड़त्वीय फ्रेम** को परिभाषित करता है जिसमें यह कानून लागू होता है।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Second Law',
        hindi: 'दूसरा नियम',
          },
          statement: {
            english: `The net external force on a body equals the rate of change of its momentum:
$$\\vec F_{\\text{net}} = \\dfrac{d \\vec p}{dt} = m \\vec a$$
(for constant mass). Both sides are vectors; SI unit of force = newton (N) = kg·m/s².`,
            hindi: 'किसी पिंड पर लगने वाला शुद्ध बाहरी बल उसके संवेग में परिवर्तन की दर के बराबर होता है: $$\\vec F_{\\text{net}} = \\dfrac{d \\vec p}{dt} = m \\vec a$$ (निरंतर द्रव्यमान के लिए)। दोनों पक्ष सदिश हैं; बल = newton (N) = kg·m/s² की SI इकाई।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Third Law',
        hindi: 'तीसरा नियम',
          },
          statement: {
            english: 'To every action there is an equal and opposite reaction. Action and reaction always act on **different bodies** and never cancel each other.',
        hindi: 'प्रत्येक क्रिया की समान एवं विपरीत प्रतिक्रिया होती है। क्रिया और प्रतिक्रिया हमेशा **अलग-अलग निकायों** पर कार्य करती हैं और कभी भी एक-दूसरे को रद्द नहीं करती हैं।',
          },
        },
        {
          type: 'definition',
          term: {
            english: 'Linear momentum',
        hindi: 'रेखीय संवेग',
          },
          body: {
            english: '$\\vec p = m \\vec v$. SI unit: kg·m/s. **Impulse** $= \\vec F \\Delta t = \\Delta \\vec p$.',
        hindi: '$\\vec p = m \\vec v$. एसआई इकाई: किग्रा · मी/से. **आवेग** $= \\vec F \\Delta t = \\Delta \\vec p$.',
          },
        },
      ],
    },
    {
      id: 'fbd',
      title: {
        english: 'Free-Body Diagrams (FBD) & Equilibrium',
        hindi: 'मुक्त-पिण्ड आरेख (FBD) और साम्यावस्था',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: `A **free-body diagram** isolates a single body and shows ALL external forces acting on it (weight, normal, tension, friction, applied). Then write Newton's second law along chosen axes:
$\\sum F_x = m a_x,\\ \\sum F_y = m a_y$.`,
            hindi: 'एक **मुक्त-शरीर आरेख** एक एकल शरीर को अलग करता है और उस पर कार्य करने वाली सभी बाहरी ताकतों (वजन, सामान्य, तनाव, घर्षण, लागू) को दिखाता है। फिर चयनित अक्षों के साथ न्यूटन का दूसरा नियम लिखें: $\\sum F_x = m a_x,\\\\\\\\ \\sum F_y = m a_y$.',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Equilibrium**: $\\sum \\vec F = 0$ (and $\\sum \\vec\\tau = 0$ for rigid bodies).',
        hindi: '**संतुलन**: $\\sum \\vec F = 0$ (और कठोर पिंडों के लिए $\\sum \\vec\\tau = 0$)।',
            },
            {
              english: '**Weight**: $\\vec W = m \\vec g$ (downward).',
        hindi: '**वजन**: $\\vec W = m \\vec g$ (नीचे की ओर)।',
            },
            {
              english: '**Normal force** $N$: contact reaction perpendicular to surface; whatever value needed to enforce no penetration.',
        hindi: '**सामान्य बल** $N$: सतह पर लंबवत संपर्क प्रतिक्रिया; बिना किसी पैठ को लागू करने के लिए जो भी मूल्य आवश्यक हो।',
            },
            {
              english: '**Tension**: scalar magnitude in a string/rope; pulls bodies it connects toward itself. For massless string, tension same throughout. Massless pulley, ideal: tension same on both sides.',
        hindi: '**तनाव**: string/rope में अदिश परिमाण; अपने से जुड़े हुए पिंडों को अपनी ओर खींचता है। द्रव्यमान रहित डोरी के लिए, संपूर्ण तनाव समान होता है। द्रव्यमान रहित चरखी, आदर्श: दोनों तरफ तनाव समान।',
            },
          ],
        },
        {
          type: 'tip',
          text: {
            english: '**Lami\'s theorem** (three concurrent forces in equilibrium): $\\dfrac{F_1}{\\sin\\alpha} = \\dfrac{F_2}{\\sin\\beta} = \\dfrac{F_3}{\\sin\\gamma}$, where $\\alpha, \\beta, \\gamma$ are angles between the other two forces.',
        hindi: '**लामी का प्रमेय** (संतुलन में तीन समवर्ती बल): $\\dfrac{F_1}{\\sin\\alpha} = \\dfrac{F_2}{\\sin\\beta} = \\dfrac{F_3}{\\sin\\gamma}$, जहां $\\alpha, \\beta, \\gamma$ अन्य दो बलों के बीच के कोण हैं।',
          },
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'A 5 kg block rests on a frictionless inclined plane at $30°$. Find normal force and acceleration down the incline. ($g = 10$ m/s²)',
        hindi: 'एक 5 किलोग्राम का ब्लॉक $30°$ पर एक घर्षण रहित झुकाव वाले विमान पर टिका हुआ है। झुकाव के नीचे सामान्य बल और त्वरण ज्ञात करें। ($g = 10$ m/s²)',
          },
          solution: {
            english: 'Along incline: $mg\\sin 30° = ma \\Rightarrow a = g\\sin 30° = 5$ m/s². Normal: $N = mg\\cos 30° = 50 \\cdot \\sqrt 3/2 = 25\\sqrt 3$ N.',
        hindi: 'ढलान के साथ: $mg\\sin 30° = ma \\Rightarrow a = g\\sin 30° = 5$ मी/से²। सामान्य: $N = mg\\cos 30° = 50 \\cdot \\sqrt 3/2 = 25\\sqrt 3$ एन.',
          },
        },
      ],
    },
    {
      id: 'pulley',
      title: {
        english: 'Pulley & Connected Body Systems',
        hindi: 'घिरनी और संयुक्त पिण्ड निकाय',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'For two masses $m_1, m_2$ connected by a light inextensible string over a frictionless massless pulley (Atwood machine):',
        hindi: 'दो द्रव्यमानों के लिए $m_1, m_2$ एक घर्षण रहित द्रव्यमान रहित चरखी (एटवुड मशीन) पर एक हल्के अवितान्य स्ट्रिंग द्वारा जुड़े हुए हैं:',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Atwood machine',
        hindi: 'एटवुड मशीन',
          },
          statement: {
            english: `Acceleration: $a = \\dfrac{(m_1 - m_2) g}{m_1 + m_2}$ (taking $m_1 > m_2$).
Tension: $T = \\dfrac{2 m_1 m_2 g}{m_1 + m_2}$.
Force on pulley axle: $2T$.`,
            hindi: 'त्वरण: $a = \\dfrac{(m_1 - m_2) g}{m_1 + m_2}$ ($m_1 > m_2$ लेते हुए)। तनाव: $T = \\dfrac{2 m_1 m_2 g}{m_1 + m_2}$. चरखी धुरी पर बल: $2T$। **घर्षण का कोण** $a = \\dfrac{(m_1 - m_2) g}{m_1 + m_2}$: $m_1 > m_2$। संपर्क प्रतिक्रिया सीमित स्थिति में सामान्य के साथ कोण $T = \\dfrac{2 m_1 m_2 g}{m_1 + m_2}$ बनाती है। **आराम का कोण** $2T$: झुकाव का कोण जिस पर एक ब्लॉक बस फिसलना शुरू करता है। . इसलिए ।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Constraint** (inextensible string): magnitudes of accelerations of two masses equal; directions opposite for a single pulley.',
        hindi: '**बाधा** (अवितान्य स्ट्रिंग): दो द्रव्यमानों के त्वरण का परिमाण बराबर; एकल चरखी के लिए विपरीत दिशाएँ।',
            },
            {
              english: 'For block + hanging mass via pulley on edge of table (block on table, mass hanging): $a = \\dfrac{m_2 g}{m_1 + m_2}$, $T = \\dfrac{m_1 m_2 g}{m_1 + m_2}$ (frictionless table).',
        hindi: 'टेबल के किनारे पर चरखी के माध्यम से ब्लॉक + लटकते द्रव्यमान के लिए (टेबल पर ब्लॉक, द्रव्यमान लटका हुआ): $a = \\dfrac{m_2 g}{m_1 + m_2}$, $T = \\dfrac{m_1 m_2 g}{m_1 + m_2}$ (घर्षण रहित टेबल)।',
            },
            {
              english: '**Spring**: $F = -kx$ (Hooke\'s law). Springs in series: $1/k_{\\text{eq}} = 1/k_1 + 1/k_2$. Parallel: $k_{\\text{eq}} = k_1 + k_2$.',
        hindi: '**वसंत**: $F = -kx$ (हुक का नियम)। श्रृंखला में स्प्रिंग्स: $1/k_{\\text{eq}} = 1/k_1 + 1/k_2$। समानांतर: $k_{\\text{eq}} = k_1 + k_2$.',
            },
          ],
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'In Atwood\'s machine, $m_1 = 6$ kg, $m_2 = 4$ kg. Find acceleration and tension. ($g = 10$ m/s²)',
        hindi: 'एटवुड की मशीन में, $m_1 = 6$ किग्रा, $m_2 = 4$ किग्रा। त्वरण और तनाव ज्ञात कीजिए। ($g = 10$ m/s²)',
          },
          solution: {
            english: '$a = (6-4)(10)/(6+4) = 2$ m/s². $T = 2(6)(4)(10)/10 = 48$ N.',
        hindi: '$a = (6-4)(10)/(6+4) = 2$ मी/से²। $T = 2(6)(4)(10)/10 = 48$ एन।',
          },
        },
      ],
    },
    {
      id: 'friction',
      title: {
        english: 'Friction',
        hindi: 'घर्षण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Static friction** $f_s$: opposes impending motion, $0 \\le f_s \\le \\mu_s N$. Self-adjusts to applied force up to maximum.',
        hindi: '**स्थैतिक घर्षण** $f_s$: आसन्न गति का विरोध करता है, $0 \\le f_s \\le \\mu_s N$। अधिकतम तक लगाए गए बल के अनुसार स्वतः समायोजित हो जाता है।',
            },
            {
              english: '**Limiting friction** (max static): $f_{s,\\max} = \\mu_s N$.',
        hindi: '**सीमित घर्षण** (अधिकतम स्थिर): $f_{s,\\max} = \\mu_s N$।',
            },
            {
              english: '**Kinetic friction** $f_k = \\mu_k N$, opposes relative motion. Generally $\\mu_k < \\mu_s$.',
        hindi: '**गतिज घर्षण** $f_k = \\mu_k N$, सापेक्ष गति का विरोध करता है। आम तौर पर $\\mu_k < \\mu_s$.',
            },
            {
              english: 'Friction is **independent of contact area** (in classical model) and depends only on normal force and surface nature.',
        hindi: 'घर्षण **संपर्क क्षेत्र से स्वतंत्र** है (शास्त्रीय मॉडल में) और केवल सामान्य बल और सतह की प्रकृति पर निर्भर करता है।',
            },
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Angle of friction & angle of repose',
        hindi: 'घर्षण का कोण और विश्राम का कोण',
          },
          statement: {
            english: `**Angle of friction** $\\phi$: $\\tan\\phi = \\mu_s$. The contact reaction makes angle $\\phi$ with normal at limiting condition.
**Angle of repose** $\\alpha$: angle of incline at which a block just begins to slide. $\\tan\\alpha = \\mu_s$. Hence $\\alpha = \\phi$.`,
            hindi: '**घर्षण का कोण** $\\phi$: $\\tan\\phi = \\mu_s$। संपर्क प्रतिक्रिया सीमित स्थिति में सामान्य के साथ कोण $\\phi$ बनाती है। **आराम का कोण** $\\alpha$: झुकाव का कोण जिस पर एक ब्लॉक बस फिसलना शुरू करता है। $\\tan\\alpha = \\mu_s$. इसलिए $\\alpha = \\phi$।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Block on incline with friction**: if angle $\\theta < \\alpha$ → block stays; if $\\theta > \\alpha$ → slides with $a = g(\\sin\\theta - \\mu_k \\cos\\theta)$.',
        hindi: '**घर्षण के साथ झुकाव पर ब्लॉक**: यदि कोण $\\theta < \\alpha$ → ब्लॉक रहता है; यदि $\\theta > \\alpha$ → $a = g(\\sin\\theta - \\mu_k \\cos\\theta)$ के साथ स्लाइड करता है।',
            },
            {
              english: '**Pulled at angle**: For block on horizontal surface pulled by force $F$ at angle $\\theta$ above horizontal, $N = mg - F\\sin\\theta$. Minimum $F$ to move: $F_{\\min} = \\dfrac{\\mu mg}{\\cos\\theta + \\mu\\sin\\theta}$, minimized at $\\tan\\theta = \\mu$.',
        hindi: '**कोण पर खींचा गया**: क्षैतिज सतह पर ब्लॉक के लिए क्षैतिज से ऊपर $\\theta$ कोण पर $F$ बल द्वारा खींचा गया, $N = mg - F\\sin\\theta$। स्थानांतरित करने के लिए न्यूनतम $F$: $F_{\\min} = \\dfrac{\\mu mg}{\\cos\\theta + \\mu\\sin\\theta}$, न्यूनतम $\\tan\\theta = \\mu$।',
            },
          ],
        },
        {
          type: 'example',
          number: 3,
          problem: {
            english: 'A 10 kg block on horizontal surface requires 30 N to just start moving. $\\mu_s$? ($g = 10$)',
        hindi: 'क्षैतिज सतह पर 10 किलो के ब्लॉक को चलना शुरू करने के लिए 30 N की आवश्यकता होती है। $\\mu_s$? ($g = 10$)',
          },
          solution: {
            english: '$\\mu_s = F/(mg) = 30/100 = 0.3$.',
        hindi: '$\\mu_s = F/(मिलीग्राम) = 30/100 = 0.3$।',
          },
        },
      ],
    },
    {
      id: 'circular-dyn',
      title: {
        english: 'Dynamics of Circular Motion',
        hindi: 'वृत्तीय गति की गतिकी',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Centripetal force',
        hindi: 'सेंट्ररपेटल फ़ोर्स',
          },
          statement: {
            english: `For a body of mass $m$ in uniform circular motion of radius $r$ at speed $v$, the net inward force is
$$F_c = \\dfrac{m v^2}{r} = m\\omega^2 r.$$
This is **provided** by tension, gravity, friction, normal etc. — not a separate fundamental force.`,
            hindi: '$m$ द्रव्यमान के एक पिंड के लिए त्रिज्या $r$ की गति $v$ पर एक समान गोलाकार गति में, शुद्ध आंतरिक बल है $$F_c = \\dfrac{m v^2}{r} = m\\omega^2 r.$$ यह तनाव, गुरुत्वाकर्षण, घर्षण, सामान्य आदि द्वारा **प्रदान** किया जाता है - कोई अलग मौलिक बल नहीं।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Conical pendulum**: $T\\cos\\theta = mg, T\\sin\\theta = mv^2/r \\Rightarrow \\tan\\theta = v^2/(rg)$.',
        hindi: '**शंक्वाकार लोलक**: $T\\cos\\theta = mg, T\\sin\\theta = mv^2/r \\Rightarrow \\tan\\theta = v^2/(rg)$.',
            },
            {
              english: '**Banked curve** (frictionless): $\\tan\\theta = v^2 / (rg)$. With friction: $v_{\\max}^2 = rg\\dfrac{\\sin\\theta + \\mu\\cos\\theta}{\\cos\\theta - \\mu\\sin\\theta}$.',
        hindi: '**बैंक्ड वक्र** (घर्षण रहित): $\\tan\\theta = v^2 / (rg)$। घर्षण के साथ: $v_{\\max}^2 = rg\\dfrac{\\sin\\theta + \\mu\\cos\\theta}{\\cos\\theta - \\mu\\sin\\theta}$.',
            },
            {
              english: '**Vertical circle** (e.g., ball on string): minimum speed at top: $v_{\\text{top}} = \\sqrt{gr}$. Minimum speed at bottom: $v_{\\text{bottom}} = \\sqrt{5gr}$.',
        hindi: '**ऊर्ध्वाधर वृत्त** (उदाहरण के लिए, स्ट्रिंग पर गेंद): शीर्ष पर न्यूनतम गति: $v_{\\text{top}} = \\sqrt{gr}$। नीचे न्यूनतम गति: $v_{\\text{bottom}} = \\sqrt{5gr}$.',
            },
            {
              english: '**Death well / loop on track**: For a car of mass $m$ to negotiate top of vertical loop without losing contact, $v_{\\text{top}} \\ge \\sqrt{gr}$.',
        hindi: '**मौत का कुआँ / ट्रैक पर लूप**: $m$ द्रव्यमान वाली कार के लिए संपर्क खोए बिना ऊर्ध्वाधर लूप के शीर्ष पर जाने के लिए, $v_{\\text{top}} \\ge \\sqrt{gr}$।',
            },
          ],
        },
        {
          type: 'example',
          number: 4,
          problem: {
            english: 'A car of mass 1000 kg negotiates a curve of radius 50 m at 36 km/h on a horizontal road. Find required coefficient of friction.',
        hindi: '1000 किलोग्राम द्रव्यमान की एक कार एक क्षैतिज सड़क पर 36 km/h पर 50 मीटर त्रिज्या के वक्र पर चलती है। घर्षण का आवश्यक गुणांक ज्ञात कीजिए।',
          },
          solution: {
            english: '$v = 10$ m/s. $\\mu \\ge v^2/(rg) = 100/(50 \\cdot 10) = 0.2$.',
        hindi: '$v = 10$ मी/से. $\\mu \\ge v^2/(rg) = 100/(50 \\cdot 10) = 0.2$।',
          },
        },
      ],
    },
    {
      id: 'pseudo',
      title: {
        english: 'Pseudo (Inertial) Forces in Non-Inertial Frames',
        hindi: 'अजड़त्वीय निर्देश तंत्रों में छद्म बल',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'In a frame accelerating with acceleration $\\vec a_0$, Newton\'s second law in that frame requires adding a **pseudo force** $\\vec F_{\\text{pseudo}} = -m \\vec a_0$ on every body of mass $m$. Then $\\sum F_{\\text{real}} + F_{\\text{pseudo}} = m \\vec a_{\\text{rel}}$.',
        hindi: 'त्वरण $\\vec a_0$ के साथ त्वरित होने वाले एक फ्रेम में, उस फ्रेम में न्यूटन के दूसरे नियम के अनुसार द्रव्यमान $m$ के प्रत्येक पिंड पर एक **छद्म बल** $\\vec F_{\\text{pseudo}} = -m \\vec a_0$ जोड़ने की आवश्यकता होती है। फिर $\\sum F_{\\text{real}} + F_{\\text{pseudo}} = m \\vec a_{\\text{rel}}$.',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Lift accelerating up** with $a$: apparent weight $= m(g + a)$ (heavier).',
        hindi: '**लिफ्ट तेज हो रही है** $a$ के साथ: स्पष्ट वजन $= m(g + a)$ (भारी)।',
            },
            {
              english: '**Lift accelerating down** with $a$: apparent weight $= m(g - a)$.',
        hindi: '**लिफ्ट नीचे की ओर तेजी से बढ़ रही है** $a$ के साथ: स्पष्ट वजन $= m(g - a)$।',
            },
            {
              english: '**Free fall** ($a = g$): apparent weight $= 0$ (weightlessness).',
        hindi: '**मुक्त गिरावट** ($a = g$): स्पष्ट वजन $= 0$ (भारहीनता)।',
            },
            {
              english: '**Centrifugal force** in rotating frame: $m\\omega^2 r$ outward (pseudo).',
        hindi: '**केन्द्रापसारक बल** घूर्णन फ्रेम में: $m\\omega^2 r$ बाहर की ओर (छद्म)।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'F = ma',
      desc: {
        english: 'Newton II',
        hindi: 'न्यूटन द्वितीय',
      },
    },
    {
      latex: '\\vec p = m \\vec v,\\ \\vec J = \\Delta \\vec p',
      desc: {
        english: 'Momentum & impulse',
        hindi: 'गति और आवेग',
      },
    },
    {
      latex: 'a_{\\text{Atwood}} = (m_1-m_2)g/(m_1+m_2)',
      desc: {
        english: '',
        hindi: 'ए एटवुड = (एम 1-एम 2)जी/(एम 1+एम 2)',
      },
    },
    {
      latex: 'f_s \\le \\mu_s N,\\ f_k = \\mu_k N',
      desc: {
        english: 'Friction',
        hindi: 'घर्षण',
      },
    },
    {
      latex: '\\tan\\alpha = \\mu_s',
      desc: {
        english: 'Angle of repose',
        hindi: 'विश्राम का कोण',
      },
    },
    {
      latex: 'F_c = mv^2/r',
      desc: {
        english: 'Centripetal',
        hindi: 'सेंट्रिपेटल',
      },
    },
    {
      latex: 'v_{\\text{top}} = \\sqrt{gr}',
      desc: {
        english: 'Vertical loop',
        hindi: 'लंबवत लूप',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Friction',
      problem: {
        english: 'A 2 kg block on a horizontal surface ($\\mu_k = 0.2$) is pushed with a horizontal force of 10 N. Find acceleration. ($g = 10$)',
        hindi: 'एक क्षैतिज सतह ($\\mu_k = 0.2$) पर 2 किलोग्राम का ब्लॉक 10 N के क्षैतिज बल के साथ धकेला जाता है। त्वरण ज्ञात कीजिए। ($g = 10$)',
      },
      answer: {
        english: '$3$ m/s²',
        hindi: '$3$ m/s²',
      },
      solution: {
        english: '$f_k = 0.2 \\cdot 2 \\cdot 10 = 4$ N. Net = $10 - 4 = 6$ N. $a = 6/2 = 3$ m/s².',
        hindi: '$f_k = 0.2 \\cdot 2 \\cdot 10 = 4$ N. नेट = $10 - 4 = 6$ N. $a = 6/2 = 3$ m/s²।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Pulley',
      problem: {
        english: 'Two blocks of mass 4 kg and 2 kg connected by a string over a frictionless pulley. Find acceleration. ($g = 10$)',
        hindi: '4 किलोग्राम और 2 किलोग्राम द्रव्यमान के दो ब्लॉक एक घर्षण रहित चरखी पर एक स्ट्रिंग द्वारा जुड़े हुए हैं। त्वरण ज्ञात करें. ($g = 10$)',
      },
      answer: {
        english: '$10/3$ m/s²',
        hindi: '$10/3$ m/s²',
      },
      solution: {
        english: '$a = (4 - 2) \\cdot 10 / (4 + 2) = 10/3$ m/s².',
        hindi: '$a = (4 - 2) \\cdot 10 / (4 + 2) = 10/3$ मी/से²।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Banking',
      problem: {
        english: 'Maximum speed on a frictionless circular track of radius 100 m banked at $30°$. ($g = 10$)',
        hindi: '100 मीटर त्रिज्या के घर्षण रहित वृत्ताकार ट्रैक पर अधिकतम गति $30°$ पर है। ($g = 10$)',
      },
      answer: {
        english: '$\\sqrt{rg \\tan\\theta} = \\sqrt{1000 \\cdot \\tan 30°} = \\sqrt{1000/\\sqrt 3} \\approx 24.0$ m/s',
        hindi: '$\\sqrt{rg \\tan\\theta} = \\sqrt{1000 \\cdot \\tan 30°} = \\sqrt{1000/\\sqrt 3} \\approx 24.0$ मी/से',
      },
      solution: {
        english: 'Frictionless banking: $v = \\sqrt{rg\\tan\\theta}$.',
        hindi: 'घर्षण रहित बैंकिंग: $v = \\sqrt{rg\\tan\\theta}$.',
      },
    },
    {
      year: 2021,
      exam: 'JEE Main',
      tag: 'Lift',
      problem: {
        english: 'A man of mass 60 kg stands on a weighing scale in a lift accelerating upward at 2 m/s². Reading? ($g = 10$)',
        hindi: '60 किलो वजन का एक आदमी 2 m/s² पर ऊपर की ओर गति कर रही एक लिफ्ट में वजन तराजू पर खड़ा है। पढ़ना? ($g = 10$)',
      },
      answer: {
        english: '$60 \\cdot 12 = 720$ N',
        hindi: '$60 \\cdot 12 = 720$ एन',
      },
      solution: {
        english: 'Apparent weight $= m(g + a)$.',
        hindi: 'स्पष्ट वजन $= m(g + a)$।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Vertical circle',
      problem: {
        english: 'A stone of mass 0.5 kg tied to a 1 m string is whirled in a vertical circle. Minimum speed at top so that string doesn\'t go slack? ($g = 10$)',
        hindi: '0.5 किलोग्राम द्रव्यमान का एक पत्थर 1 मीटर की डोरी से बांधकर एक ऊर्ध्वाधर वृत्त में घुमाया जाता है। शीर्ष पर न्यूनतम गति ताकि स्ट्रिंग ढीली न हो जाए? ($g = 10$)',
      },
      answer: {
        english: '$\\sqrt{10}$ m/s ≈ 3.16 m/s',
        hindi: '$\\sqrt{10}$ मी/से ≈ 3.16 मी/से',
      },
      solution: {
        english: 'At top, $T \\ge 0$, so $mg = mv^2/r$ gives $v_{\\min} = \\sqrt{gr}$.',
        hindi: 'शीर्ष पर, $T \\ge 0$, इसलिए $mg = mv^2/r$ $v_{\\min} = \\sqrt{gr}$ देता है।',
      },
    },
  ],
  next: {
    slug: 'work-energy-power',
    title: 'Work, Energy & Power',
  },
};

export default chapter;
